import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: listRef, isVisible: listVisible } = useScrollAnimation();

  const faqs = [
    {
      question: 'How much can I save with solar panels in Florida?',
      answer:
        'Florida homeowners typically save 50-70% on their electricity bills with solar. With an average system size of 8-10kW, you can expect to save $100-150 per month, which adds up to $1,200-1,800 annually. Combined with the 30% federal tax credit and Florida incentives, most systems pay for themselves in 6-8 years.',
    },
    {
      question: 'What is the difference between a heat pump and an air conditioner?',
      answer:
        "A heat pump provides both heating and cooling, making it more versatile and energy‑efficient than a traditional AC unit. In Florida's mild winters, heat pumps are 2-3 times more efficient than electric resistance heating. They work by transferring heat rather than generating it, resulting in lower energy bills year‑round.",
    },
    {
      question: 'How long does installation take?',
      answer:
        'Solar panel installation typically takes 1-3 days depending on system size. HVAC replacement usually takes 1 day. Attic insulation can be completed in 4-8 hours. Solar attic fan installation takes 2-4 hours. We coordinate all installations to minimize disruption to your daily routine.',
    },
    {
      question: 'What rebates and incentives are available in Florida?',
      answer:
        'Florida homeowners can access multiple incentives: 30% Federal Solar Tax Credit (through 2032), Florida Solar Rights Act property tax exemption, utility company rebates up to $1,500 for HVAC, federal tax credits up to $2,000 for heat pumps, and up to $1,200 annually for insulation improvements under the Inflation Reduction Act.',
    },
    {
      question: 'Do you offer financing options?',
      answer:
        'Yes! We offer flexible financing with $0 down, low monthly payments, and terms up to 25 years. We work with multiple lenders to find the best rates for your situation. Many customers find their monthly loan payment is less than their energy savings, making upgrades cash‑flow positive from day one.',
    },
    {
      question: "How does attic insulation help in Florida's hot climate?",
      answer:
        'Attic insulation is crucial in Florida because attics can reach 150°F in summer. Proper R-30 insulation creates a thermal barrier that prevents heat from radiating into your living space, reducing your AC workload by 30-40%. This means lower energy bills, more consistent temperatures, and extended HVAC lifespan.',
    },
  ];

  const handleToggle = (index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6">
        <div
          ref={headerRef}
          className="text-center mb-10 lg:mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="inline-block bg-brand-navy-light text-brand-navy px-4 py-2 rounded-full text-sm font-semibold mb-4">
            COMMON QUESTIONS
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4">
            Everything You Need To Know
          </h2>
        </div>

        <div
          ref={listRef}
          className="space-y-3 sm:space-y-4 mb-10 lg:mb-12"
          style={{
            opacity: listVisible ? 1 : 0,
            transform: listVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s',
          }}
        >
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-brand-cream rounded-2xl overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => handleToggle(index)}
                className="w-full px-5 sm:px-8 py-4 sm:py-6 flex items-center justify-between text-left cursor-pointer hover:bg-brand-sand transition-colors"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <span className="font-semibold text-gray-900 text-base sm:text-lg pr-4">
                  {faq.question}
                </span>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`${
                      openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                    } text-brand-red text-2xl transition-transform duration-300`}
                  ></i>
                </div>
              </button>

              <div
                id={`faq-answer-${index}`}
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-5 sm:px-8 pb-4 sm:pb-6 text-gray-600 leading-relaxed text-sm sm:text-base">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="text-center"
          style={{
            opacity: listVisible ? 1 : 0,
            transform: listVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.3s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.3s',
          }}
        >
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 bg-brand-red text-white px-8 py-4 rounded-full font-semibold hover:bg-brand-red-dark transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            View All FAQs
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
